// STAREA INSTALAȚIILOR pe un ecran: modelul arătat, ce sisteme se văd și ce
// element e ales. O ține pagina (`pages/Instalatii.tsx`); vizualizatorul o
// citește, ca să deseneze straturile în 2D și 3D, iar panoul ca să arate
// verificările. Fără furnizor, `useInstalatii()` e `null` și vizualizatorul
// rămâne cum era — așa poate primi stratul orice alt ecran, mai târziu.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { TOATE_STRATURILE, type ModelInstalatii, type Strat } from "./model";

export interface StareInstalatii {
  model: ModelInstalatii;
  /** Straturile aprinse: sistemele și, la electrice, tipurile de circuit. */
  vizibile: ReadonlySet<Strat>;
  comuta(straturi: readonly Strat[], aprins: boolean): void;
  ales: string | null;
  alege(id: string | null): void;
  /** Segmentele aprinse pe lângă cel ales: ruta lui până la colector
   *  (`rutaSpreColector`), când omul o cere din panou. Un clic pe altceva o
   *  stinge. */
  evidentiate: ReadonlySet<string>;
  evidentiaza(ids: Iterable<string>): void;
  /** Planul 2D (camerele cu numele și aria lor, conturul mobilierului) desenat
   *  pe pardoseala machetei 3D, ca țevile să se citească față de WC, lavoar,
   *  bucătărie — nu față de o podea goală. */
  planPeJos: boolean;
  comutaPlanPeJos(): void;
  /** Contururile apartamentelor desenate peste instalații, ca fiecare
   *  apartament să se citească separat, de la racordul lui la ultima piesă. */
  apartamente: boolean;
  comutaApartamente(): void;
  /** Numai instalațiile apartamentului elementului ales: restul se estompează. */
  doarApartamentul: boolean;
  comutaDoarApartamentul(): void;
  /** Apartamentul elementului ales (al segmentului sau al echipamentului), sau `null`. */
  apartamentAles: string | null;
}

const Ctx = createContext<StareInstalatii | null>(null);

export function FurnizorInstalatii({ model, children }: { model: ModelInstalatii; children: ReactNode }) {
  const [vizibile, setVizibile] = useState<ReadonlySet<Strat>>(() => new Set(TOATE_STRATURILE));
  const [ales, setAles] = useState<string | null>(null);
  const [evidentiate, setEvidentiate] = useState<ReadonlySet<string>>(() => new Set());
  const [planPeJos, setPlanPeJos] = useState(true);
  const [apartamente, setApartamente] = useState(true);
  const [doarApartamentul, setDoarApartamentul] = useState(false);
  const comuta = useCallback((straturi: readonly Strat[], aprins: boolean) => setVizibile((v) => {
    const nou = new Set(v);
    for (const s of straturi) { if (aprins) nou.add(s); else nou.delete(s); }
    return nou;
  }), []);
  const alege = useCallback((id: string | null) => { setAles(id); setEvidentiate(new Set()); }, []);
  const evidentiaza = useCallback((ids: Iterable<string>) => setEvidentiate(new Set(ids)), []);
  const comutaPlanPeJos = useCallback(() => setPlanPeJos((v) => !v), []);
  const comutaApartamente = useCallback(() => setApartamente((v) => !v), []);
  const comutaDoarApartamentul = useCallback(() => setDoarApartamentul((v) => !v), []);
  const apartamentAles = useMemo(() => {
    if (!ales) return null;
    return model.segmente.find((s) => s.id === ales)?.flat ?? model.echipamente.find((e) => e.id === ales)?.flat ?? null;
  }, [model, ales]);
  const valoare = useMemo<StareInstalatii>(
    () => ({ model, vizibile, comuta, ales, alege, evidentiate, evidentiaza, planPeJos, comutaPlanPeJos,
             apartamente, comutaApartamente, doarApartamentul, comutaDoarApartamentul, apartamentAles }),
    [model, vizibile, comuta, ales, alege, evidentiate, evidentiaza, planPeJos, comutaPlanPeJos,
     apartamente, comutaApartamente, doarApartamentul, comutaDoarApartamentul, apartamentAles]);
  return <Ctx.Provider value={valoare}>{children}</Ctx.Provider>;
}

export const useInstalatii = () => useContext(Ctx);
